import PlatformBaseController from "./platform_base_controller";
import {
    appendActiveOnly,
    resetActiveOnly,
    setActiveOnly,
    visibleActiveOnlyInput,
} from "./mixins/active_only_filter";

// Handles KB/date filters for the User Inquiry Metrics workspace
export default class extends PlatformBaseController {
    static targets = [
        "filterForm",
        "filterSubmit",
        "logStartDate",
        "logEndDate"
    ];

    static values = {
        initDates: { type: Boolean, default: true }
    };
    connect() {
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;
        this.skipDefaultDates = false;
        this.bootstrapInitialDates();
    }

    filterFormTargetConnected(target) {
        this.bootstrapInitialDates(target);
    }

    bootstrapInitialDates(form = null) {
        const forms   = this.allFilterForms(form);
        const primary = forms.find((scope) => scope?.querySelector?.('[name="logs_start_date"]')) || forms[0];
        if (!primary) return;

        if (!this.shouldInitializeDates(primary)) {
            const start = primary.querySelector?.('[name="logs_start_date"]')?.value || "";
            const end   = primary.querySelector?.('[name="logs_end_date"]')?.value   || "";
            this.initialDates = { startYMD: start, endYMD: end };
            return;
        }

        const { startYMD, endYMD } = this.ensureFilterDates({ form: primary });
        this.initialDates = { startYMD, endYMD };
    }

    allFilterForms(preferred = null) {
        const forms = [];
        const push = (candidate) => {
            if (candidate && !forms.includes(candidate)) forms.push(candidate);
        };

        push(preferred);
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach(push);
        push(this.element.closest?.("form"));
        push(this.element);
        return forms;
    }

    shouldInitializeDates(form = null) {
        const datasetKey = this.datasetKeyFor("InitDatesValue");
        const sources    = [];

        if (form) sources.push(form.dataset?.[datasetKey]);
        if (this.hasFilterFormTarget) this.filterFormTargets.forEach((target) => sources.push(target.dataset?.[datasetKey]));
        sources.push(this.element.dataset?.[datasetKey]);

        for (const value of sources) {
            const parsed = this.parseBooleanDataset(value);
            if (parsed !== undefined) return parsed;
        }

        return this.initDatesValue;
    }

    parseBooleanDataset(value) {
        if (value === undefined || value === null || value === "") return undefined;
        if (typeof value === "boolean") return value;

        const normalised = String(value).toLowerCase();
        if (["false", "0", "no", "off"].includes(normalised)) return false;
        if (["true", "1", "yes", "on"].includes(normalised)) return true;
        return undefined;
    }

    datasetKeyFor(suffix) {
        return `${this.identifierCamelCase}${suffix}`;
    }

    get identifierCamelCase() {
        if (!this._identifierCamelCase) {
            this._identifierCamelCase = this.identifier.replace(/-([a-z])/g, (_m, chr) => chr.toUpperCase());
        }
        return this._identifierCamelCase;
    }

    setDates({ startYMD, endYMD }, form = null) {
        const forms = this.allFilterForms(form);

        forms.forEach((scope) => {
            if (!scope || typeof scope.querySelector !== "function") return;
            const startInput = scope.querySelector('[name="logs_start_date"]');
            const endInput   = scope.querySelector('[name="logs_end_date"]');

            if (startInput) startInput.value = startYMD != null ? startYMD : "";
            if (endInput)   endInput.value   = endYMD   != null ? endYMD   : "";
        });

        if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = startYMD != null ? startYMD : ""; });
        if (this.hasLogEndDateTarget)   this.logEndDateTargets.forEach((input) => { input.value   = endYMD   != null ? endYMD   : ""; });

        if (startYMD !== undefined) this.setPersistentFilter("logs_start_date", startYMD != null ? startYMD : "");
        if (endYMD   !== undefined) this.setPersistentFilter("logs_end_date",   endYMD   != null ? endYMD   : "");
    }

    ensureFilterDates({ event = null, form = null, period = 30 } = {}) {
        const fallbackForm =
            event?.target?.closest?.("form") ||
            event?.target?.form ||
            (this.hasFilterFormTarget ? this.filterFormTargets[0] : null) ||
            this.element.closest?.("form") ||
            this.element;

        const scopeForms = this.allFilterForms(form || fallbackForm);

        const today        = this.startOfLocalDay(new Date());
        const defaultEnd   = this.formatYMD(today);
        const defaultStart = this.formatYMD(this.addDays(today, -period));

        let resolvedStart = "";
        let resolvedEnd   = "";

        scopeForms.forEach((scope) => {
            if (!scope || typeof scope.querySelector !== "function") return;

            const startInput = scope.querySelector('[name="logs_start_date"]');
            const endInput   = scope.querySelector('[name="logs_end_date"]');

            if (startInput) {
                if (!startInput.value) startInput.value = defaultStart;
                if (!resolvedStart && startInput.value) resolvedStart = startInput.value;
            }

            if (endInput) {
                if (!endInput.value) endInput.value = defaultEnd;
                if (!resolvedEnd && endInput.value) resolvedEnd = endInput.value;
            }
        });

        if (resolvedStart) this.setPersistentFilter("logs_start_date", resolvedStart);
        if (resolvedEnd)   this.setPersistentFilter("logs_end_date",   resolvedEnd);

        if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = resolvedStart || ""; });
        if (this.hasLogEndDateTarget)   this.logEndDateTargets.forEach((input) => { input.value   = resolvedEnd   || ""; });

        return {
            startYMD: resolvedStart || "",
            endYMD:   resolvedEnd   || "",
            start:    resolvedStart ? this.toLocalDateOnly(resolvedStart) : undefined,
            end:      resolvedEnd ? this.toLocalDateOnly(resolvedEnd)   : undefined
        };
    }

    selectProject(event) {
        const projectId = event.target.value;
        this.setPersistentFilter("project_id", projectId);
        this.updateProjectIdInputs(projectId);

        const form   = this.filterFormTargets[0] || this.element.closest?.("form") || this.element;
        const params = this.buildFilterParams(form);
        const activeOnlyInput = visibleActiveOnlyInput();
        appendActiveOnly(params, { root: activeOnlyInput?.closest("form") || form || document });
        if (projectId) params.set("project_id", projectId);

        this.fetchAction(`/insights/user_inquiry_metrics/filter_parameters?${params.toString()}`);
    }

    updateProjectIdInputs(projectId) {
        this.allFilterForms().forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const hidden = form.querySelector('[name="project_id"]');
            if (hidden) hidden.value = projectId;
        });
    }

    syncDateInputs(fieldName, value, exclude = null) {
        this.allFilterForms().forEach((form) => {
            if (!form || typeof form.querySelector !== "function") return;
            const input = form.querySelector(`[name="${fieldName}"]`);
            if (input && input !== exclude) input.value = value;
        });
    }

    removeFilterTag(event) {
        event.preventDefault();

        const clickedElement = event.currentTarget;
        const filterTag      = clickedElement.dataset.filterTagValue;

        if (filterTag) this.deletePersistentFilter(filterTag);

        if (filterTag === "logs_start_date") {
            if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_start_date", "");
            this.skipDefaultDates = true;
        }
        if (filterTag === "logs_end_date") {
            if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_end_date", "");
            this.skipDefaultDates = true;
        }
        if (filterTag === "active_only") {
            setActiveOnly(false);
        }
        clickedElement.remove();
    }

    clearFilters(event) {
        event.preventDefault();

        const form      = event.target.closest("form") || event.target.form || this.element;
        const projectId = this.getPersistedFilter(form, "project_id");

        this.deletePersistentFilter("logs_start_date");
        this.deletePersistentFilter("logs_end_date");

        const today = this.startOfLocalDay(new Date());
        const defaultEnd = this.formatYMD(today);
        const defaultStart = this.formatYMD(this.addDays(today, -30));
        const defaultDates = { startYMD: defaultStart, endYMD: defaultEnd };
        this.setDates(defaultDates, form);
        this.initialDates = defaultDates;
        this.skipDefaultDates = false;

        const params = new URLSearchParams();
        if (projectId) params.set("project_id", projectId);
        resetActiveOnly();
        appendActiveOnly(params, { root: form });

        this.fetchAction(`/insights/user_inquiry_metrics/clear_filters?${params.toString()}`);
    }

    setFilters(event) {
        event.preventDefault();
        if (this.hasFilterSubmitTarget) this.filterSubmitTarget.disabled = false;

        const form      = event.target.closest?.("form") || event.target.form || this.element;
        const projectId = this.getPersistedFilter(form, "project_id");
        const start     = this.getPersistedFilter(form, "logs_start_date");
        const end       = this.getPersistedFilter(form, "logs_end_date");

        if (projectId !== undefined) this.setPersistentFilter("project_id", projectId);

        if (start === undefined) {
            if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_start_date", "");
        }
        if (end === undefined) {
            if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((input) => { input.value = ""; });
            this.syncDateInputs("logs_end_date", "");
        }

        if (!this.skipDefaultDates && this.shouldInitializeDates(form)) this.ensureFilterDates({ form });

        const params = this.buildFilterParams(form);
        appendActiveOnly(params, { root: form });
        this.fetchAction(`${form.action || "/insights/user_inquiry_metrics/filter_parameters"}?${params.toString()}`);
    }

    updateFilterDate(event) {
        const input = event.target;
        const { name, value } = input;
        if (name === "logs_start_date" || name === "logs_end_date") {
            if (value) {
                this.setPersistentFilter(name, value);
            } else {
                this.deletePersistentFilter(name);
            }

            if (name === "logs_start_date") {
                if (this.hasLogStartDateTarget) this.logStartDateTargets.forEach((target) => { target.value = value; });
                this.syncDateInputs(name, value, input);
            }

            if (name === "logs_end_date") {
                if (this.hasLogEndDateTarget) this.logEndDateTargets.forEach((target) => { target.value = value; });
                this.syncDateInputs(name, value, input);
            }
        }
    }

    buildFilterParams(form) {
        const params = new URLSearchParams();
        const projectId = this.getPersistedFilter(form, "project_id");
        const start     = this.getPersistedFilter(form, "logs_start_date");
        const end       = this.getPersistedFilter(form, "logs_end_date");

        if (projectId) params.set("project_id", projectId);
        if (start !== undefined) params.set("logs_start_date", start || "");
        if (end   !== undefined) params.set("logs_end_date",   end   || "");

        return params;
    }

}
